import * as THREE from "three";
import TWEEN from "@tweenjs/tween.js";
import { OrbitControls } from "./controls/OrbitControls";
import { Sky } from "three-stdlib";
import Boat from "./Boat";
import Grass from "./utils/Grass";
import OceanHotspot from "./OceanHotspot";
import BillboardHotspot from "./BillboardHotspot";

export default class SceneIntro extends THREE.Scene {
  constructor(player, config, repeat = false) {
    super();

    this.player = player;
    this.config = config;
    this.hotspots = [];
    this.boatScale = 1;
    const renderTargetParams = {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBFormat,
      stencilBuffer: false,
    };
    /**
     * FBO for this scene
     */
    this.fbo = new THREE.WebGLRenderTarget(
      window.innerWidth,
      window.innerHeight,
      renderTargetParams,
    );

    this.boat = new Boat(
      {
        ...config,
        disableRocking: true,
      },
      () => this.finishedLoad(),
      this,
    );

    const width = this.player.container.clientWidth;
    const height = this.player.container.clientHeight;

    this.player.renderer.setScissor(0, 0, width, height);

    for (const hotspot of config.hotspots) {
      if (hotspot.type === "billboard") {
        const h = new BillboardHotspot(hotspot);
        this.hotspots.push(h);
        this.boat.add(h);
      } else {
        const h = new OceanHotspot(hotspot);
        h.setScale(0.4);
        this.hotspots.push(h);

        this.boat.add(h);
      }
    }

    // Add sky
    this.sky = new Sky();
    this.sky.scale.setScalar(10000);

    // Add ambient light to stop the 'dark side of the boat'
    this.ambLight = new THREE.AmbientLight(0xffffff, 0.5);

    this.loadingCallback = player.loadingManager.start(!repeat);
    this.loadCount = 1;

    this.grass = new Grass(player);
    this.grass.rotation.x = -Math.PI / 2;
    this.grass.receiveShadow = true;

    // Create directional light
    // This becomes the sun
    this.light = new THREE.DirectionalLight(0xffffff, 0.8);
    this.light.castShadow = true;
    this.light.shadow.bias = -0.002;
    this.light.position.set(0, 100, 0);
    this.light.shadow.camera.top = 40;
    this.light.shadow.camera.bottom = -40;
    this.light.shadow.camera.left = 40;
    this.light.shadow.camera.right = -40;

    // TODO needs moving
    player.renderer.autoClear = false;
    player.renderer.shadowMap.enabled = true;
    player.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    player.controlsOrbit.enabled = false;

    // Setup sky uniforms
    const uniforms = this.sky.material.uniforms;
    uniforms.turbidity.value = 10;
    uniforms.rayleigh.value = 2;
    uniforms.mieCoefficient.value = 0.005;
    uniforms.mieDirectionalG.value = 0.8;

    // Position of the sun
    this.skyParams = {
      distance: 400,
      inclination: 0.15,
      azimuth: 0.205,
    };

    // Set initial sun position
    this.updateSky();

    // Create camera that is only used in this scene
    this.camera = new THREE.PerspectiveCamera(
      55,
      window.innerWidth / window.innerHeight,
      1,
      20000,
    );
    const initialPosition = { x: 250, y: 120, z: -90 };

    this.camera.position.set(
      initialPosition.x,
      initialPosition.y,
      initialPosition.z,
    );
    this.camera.lookAt(0, 10, 0);

    // As we use a different camera, we need to handle resize here too
    window.addEventListener("resize", this.onWindowResize.bind(this), false);

    this.controls = new OrbitControls(this.camera, player.renderer.domElement);
    this.controls.mainCamera = this.camera;
    this.controls.maxPolarAngle = Math.PI * 0.495;
    this.controls.target.set(0, 10, 0);
    this.controls.minDistance = 0;
    this.controls.maxDistance = 200;
    this.controls.enablePan = false;
    this.controls.enableDamping = true;
    // this.controls.dampingFactor = 0.01;
    this.controls.rotateSpeed = 1.0;
    this.camera.lookAt(this.controls.target);
  }

  /**
   * This is triggered when window is resized.
   * It's used to change the camera aspect.
   * We already update the renderer in Player
   */
  onWindowResize() {
    const width = this.player.container.clientWidth;
    const height = this.player.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.updateBoat();
  }

  updateBoat() {
    if (this.boat) {
      const width = this.player.container.clientWidth;
      const height = this.player.container.clientHeight;
      const ratio = width / height;

      const defaultScale = 32 / this.boat.maxPosition;
      const scale = Math.min(defaultScale * 1.5, defaultScale * ratio);

      this.boatScale = scale;

      this.boat.scale.setScalar(scale);
      // this.boat.position.y = -2.2 * scale;
    }
  }

  /**
   * Update sky updates the light position based on sky params
   */
  updateSky() {
    const theta = Math.PI * (this.skyParams.inclination - 0.5);
    const phi = 2 * Math.PI * (this.skyParams.azimuth - 0.5);

    this.light.position.x = this.skyParams.distance * Math.cos(phi);
    this.light.position.y =
      this.skyParams.distance * Math.sin(phi) * Math.sin(theta);
    this.light.position.z =
      this.skyParams.distance * Math.sin(phi) * Math.cos(theta);

    this.sky.material.uniforms.sunPosition.value = this.light.position.copy(
      this.light.position,
    );
  }

  /**
   * When an asset load is finished, call this.
   * This will hide the loading container
   */
  finishedLoad() {
    this.loadCount -= 1;

    if (this.loadCount === 0) {
      this.updateBoat();
      this.start();
      this.loadingCallback();
    }
  }

  /**
   * Called every frame before render
   */
  update() {
    this.boat.update();
  }

  /**
   * Simply renders the scene.
   */
  render(renderer, camera, useFbo) {
    // this.controls.update();
    if (useFbo) {
      renderer.render(this, this.camera, this.fbo, true);
    } else {
      renderer.render(this, this.camera);
    }
  }

  /**
   * Start is called when load is complete and it's time to begin!
   */
  start() {
    // Add everything to the scene
    this.add(this.boat);
    this.add(this.grass);
    this.add(this.sky);
    this.add(this.ambLight);
    this.add(this.light);

    // for (const hotspot of this.hotspots) {
    //   this.add(hotspot);
    // }

    const finalPosition = {
      x: 0,
      y: 55,
      z: -80,
    };

    const tween = new TWEEN.Tween(this.camera.position).to(finalPosition, 3000);
    tween.onUpdate(() => {
      this.camera.lookAt(0, 10, 0);
    });
    tween.easing(TWEEN.Easing.Quintic.InOut);
    tween.start();
  }

  /**
   * Called just before deleting.
   * Used to clean up!
   */
  destroy() {
    window.removeEventListener("resize", this.onWindowResize.bind(this), false);
  }

  onMouseUp(event, mouse, camera) {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);

    // Check if any hotspots are hit
    const intersects = raycaster.intersectObjects(this.hotspots, true);

    if (intersects.length > 0) {
      const intersect = intersects[0];
      const object = intersects[0].object;
      const image = object.parent.getImage();
      intersect.position = intersect.point;

      const start = new THREE.Euler().copy(this.camera.rotation);
      this.camera.lookAt(intersect.point);
      const end = new THREE.Euler().copy(this.camera.rotation);
      this.camera.rotation.copy(start);

      if (Math.abs(start.z - end.z) > Math.PI) {
        if (start.z < end.z) {
          end.z -= Math.PI * 2;
        } else {
          end.z += Math.PI * 2;
        }
      }

      // const current = {
      //   x: start.x,
      //   y: start.y,
      //   z: start.z,
      // };

      // new TWEEN.Tween(current).to({
      //     x: end.x,
      //     y: end.y,
      //     z: end.z,
      // }, 1000)
      // .easing(TWEEN.Easing.Quadratic.InOut)
      // .onUpdate(() => {
      //     this.camera.rotation.x = current.x;
      //     this.camera.rotation.y = current.y;
      //     this.camera.rotation.z = current.z;
      // })
      // .start();

      setTimeout(() => {
        this.target = object.position;

        // This is used because the boat is currently flipped.

        // How much before or after the camera position should be
        // let offset = 0.25;

        // if (this.target.x <= 0) {
        //   offset *= -1;
        // }

        // Final position for the camera
        const finalPosition = {
          x: -this.target.x * this.boatScale,
          y: this.target.y * this.boatScale,
          z: -this.target.z * this.boatScale,
        };

        // Camera starts at looking towards the center
        const lookAt = {
          x: 0,
          y: 10,
          z: 0,
        };

        // Tween the position of the camera towards the final position
        const tween = new TWEEN.Tween(this.camera.position).to(
          finalPosition,
          4000,
        );
        tween.onUpdate(() => {
          // This tween ends after the lookAt tween, so much keep looking at.
          this.camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
        });
        tween.onComplete(() => {
          this.player.transitionTo(image, intersect);
        });
        tween.easing(TWEEN.Easing.Quartic.InOut);
        tween.start();

        // This causes the camera to point in the wrong direction, not sure
        // why this code was added. I've commented it out for the time being
        // -snewman

        // // Location of the node
        // const targetAt = {
        //   x: -this.target.x * this.boatScale,
        //   y: this.target.y * this.boatScale,
        //   z: -this.target.z * this.boatScale,
        // };

        // // Tweens the look at value for move camera movement
        // const rTween = new TWEEN.Tween(lookAt).to(targetAt, 2500);
        // rTween.onUpdate(() => {
        //   this.camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
        // });
        // rTween.easing(TWEEN.Easing.Quartic.InOut);
        // rTween.start();
      }, 250);
    }
  }

  /**
   * Not currently used
   */
  startTransition() {}

  onMouseMove(event, mouse, camera) {}

  hotspotsUnderMouse() {}

  static injectExample(tour) {
    tour.data.intro = {
      type: "intro",
      model: `${window.location.origin}/tour/assets/digger.obj`,
      materials: `${window.location.origin}/tour/assets/digger.mtl`,
      hotspots: [
        { x: 0.15, y: 0.06, z: -0.3, image: 1018, type: "billboard" },
        { x: 0.3, y: 0.06, z: 0.25, image: 1037, type: "billboard" },
        { x: -0.45, y: 0.06, z: -0.15, image: 1038, type: "billboard" },
        { x: -0.6, y: 0.06, z: 0.2, image: 1039, type: "billboard" },
        { x: 0.18, y: 0.22, z: 0.08, image: 1017 },
      ],
    };
  }
}
